import React, { useState, useEffect } from 'react';
import { Save, Upload, X, Plus, Trash2, Calendar, Check, AlertCircle, FileText, Image, User, Mail, MapPin, Briefcase, DollarSign, Award, Globe, Shield } from 'lucide-react';
import { candidatesAPI, jobsAPI, clientsAPI } from "../../services/api";
import { toast } from "react-toastify";
import { handleDuplicateError, formatDuplicateMessage } from '../../utils/duplicateCheckUtils';

// Helper function to format field name
const formatFieldName = (fieldName) => {
  if (!fieldName) return '';
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

// Field type and options detector based on field name
const getFieldTypeAndOptions = (fieldName) => {
  const lowerField = fieldName.toLowerCase();

  if (lowerField.includes('consent') || lowerField.includes('agree') || lowerField.includes('authorization') ||
    lowerField.includes('permission') || lowerField.includes('verified') || lowerField.includes('approved') ||
    lowerField.includes('confirm')) {
    return { fieldType: 'select', options: ['Yes', 'No'] };
  }
  if (lowerField === 'gender') return { fieldType: 'select', options: ['Male', 'Female', 'Other', 'Prefer not to say'] };
  if (lowerField.includes('marital')) return { fieldType: 'select', options: ['Single', 'Married', 'Divorced', 'Widowed', 'Prefer not to say'] };
  if (lowerField.includes('education') && lowerField.includes('level')) {
    return { fieldType: 'select', options: ['High School', 'Associate Degree', 'Bachelor\'s Degree', 'Master\'s Degree', 'Doctorate', 'Other'] };
  }
  if (lowerField.includes('work') && lowerField.includes('authorization')) {
    return { fieldType: 'select', options: ['Citizen', 'Permanent Resident', 'Work Visa', 'Student Visa', 'Other'] };
  }
  if (lowerField.includes('visa') && lowerField.includes('status')) {
    return { fieldType: 'select', options: ['H1B', 'L1', 'F1', 'OPT', 'CPT', 'Green Card', 'Citizen', 'Other'] };
  }
  if (lowerField.includes('employment') && lowerField.includes('type')) {
    return { fieldType: 'select', options: ['Full-time', 'Part-time', 'Contract', 'Freelance', 'Internship'] };
  }
  if (lowerField.includes('salary') && lowerField.includes('frequency')) {
    return { fieldType: 'select', options: ['Annual', 'Monthly', 'Hourly', 'Daily'] };
  }
  if (lowerField.includes('notice') && lowerField.includes('period')) {
    return { fieldType: 'select', options: ['Immediate', '15 Days', '30 Days', '45 Days', '60 Days', '90 Days', 'Negotiable'] };
  }
  if (lowerField.includes('relocate') || lowerField.includes('relocation')) {
    return { fieldType: 'select', options: ['Yes', 'No', 'Maybe'] };
  }
  if (lowerField.includes('currency')) {
    return { fieldType: 'select', options: ['USD', 'INR', 'EUR', 'GBP', 'CAD', 'AUD', 'SGD', 'Other'] };
  }
  if (lowerField === 'country' || lowerField.includes('nationality')) {
    return { fieldType: 'select', options: ['India', 'United States', 'United Kingdom', 'Canada', 'Australia', 'Singapore', 'UAE', 'Other'] };
  }
  if (lowerField === 'state' && !lowerField.includes('country')) {
    return { fieldType: 'select', options: ['Andhra Pradesh', 'Telangana', 'Karnataka', 'Tamil Nadu', 'Kerala', 'Maharashtra', 'Gujarat', 'Rajasthan', 'Delhi', 'Other'] };
  }
  if (lowerField.includes('source')) {
    return { fieldType: 'select', options: ['Direct Application', 'Referral', 'LinkedIn', 'Job Portal', 'Campus', 'Agency', 'Other'] };
  }
  if (lowerField.includes('email')) return { fieldType: 'email', options: [] };
  if (lowerField.includes('phone') || lowerField.includes('mobile') || lowerField.includes('whatsapp')) return { fieldType: 'tel', options: [] };
  if (lowerField.includes('years') || lowerField.includes('experience') || lowerField.includes('ctc') ||
    lowerField.includes('salary') || lowerField.includes('age') || lowerField.includes('gpa') || lowerField.includes('score')) {
    return { fieldType: 'number', options: [] };
  }
  if (lowerField.includes('date') || lowerField === 'dob' || lowerField === 'actualjoiningdate') return { fieldType: 'date', options: [] };
  if (lowerField.includes('url') || lowerField.includes('website') || lowerField.includes('linkedin') ||
    lowerField.includes('github') || lowerField.includes('portfolio')) {
    return { fieldType: 'url', options: [] };
  }
  if (lowerField.includes('address') || lowerField.includes('description') || lowerField.includes('summary') ||
    lowerField.includes('notes') || lowerField.includes('comments') || lowerField.includes('about') ||
    lowerField.includes('explanation') || lowerField.includes('additional')) {
    return { fieldType: 'textarea', options: [] };
  }
  return { fieldType: 'text', options: [] };
};

const BOOLEAN_FIELD_NAMES = [
  'backgroundCheckConsent', 'drugTestConsent', 'rightToWorkConfirm', 'dataProcessingConsent',
  'communicationsConsent', 'termsAndConditionsConsent', 'creditCheckConsent',
  'employmentVerificationConsent', 'educationVerificationConsent', 'willingToRelocate',
];

const CandidateCreateModel = ({ onClose, onSave, jobId: propJobId, clientId: propClientId }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fieldConfigs, setFieldConfigs] = useState([]);
  const [screeningQuestions, setScreeningQuestions] = useState([]);
  const [screeningAnswers, setScreeningAnswers] = useState({});
  const [formDataState, setFormDataState] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({
    resume: null, coverLetter: null, profilePhoto: null,
    certifications: [], supportingDocuments: [], professionalLicenses: null, areaCertifications: null
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [organizationId, setOrganizationId] = useState(null);

  // ── Client state (loaded first) ──────────────────────────────────────────
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(propClientId || '');

  // ── Job state (loaded after client selected) ─────────────────────────────
  const [jobPostings, setJobPostings] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(propJobId || '');
  const [selectedJobCode, setSelectedJobCode] = useState('');
  const [selectedJobObject, setSelectedJobObject] = useState(null);

  const [technicalSkills, setTechnicalSkills] = useState([{ skillName: '', rating: 3 }]);
  const [softSkills, setSoftSkills] = useState([{ skillName: '', rating: 3 }]);
  const [languages, setLanguages] = useState([{ languageName: '', canRead: false, canWrite: false, canSpeak: false }]);
  const [preferredLocations, setPreferredLocations] = useState(['']);
  const [keyAchievements, setKeyAchievements] = useState(['']);
  const [awardsRecognition, setAwardsRecognition] = useState(['']);

  const EXCLUDED_FIELDS = [
    'technicalSkills', 'softSkills', 'languages', 'preferredLocations', 'keyAchievements',
    'awardsRecognition', 'resumeUrl', 'coverLetterUrl', 'profilePhotoUrl', 'certificationsUpload',
    'supportingDocuments', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy', 'id', 'organizationId',
    'jobIds', 'applicationId', 'clientIds', 'status', 'screeningQuestionsByJob',
    'professionalLicenses', 'areaProfessionalCertifications'
  ];

  const [availableDocumentFields, setAvailableDocumentFields] = useState({
    resumeUrl: false, coverLetterUrl: false, profilePhotoUrl: false,
    certificationsUpload: false, supportingDocuments: false,
    professionalLicenses: false, areaProfessionalCertifications: false
  });

  const [availableSpecialFields, setAvailableSpecialFields] = useState({
    technicalSkills: false, softSkills: false, languages: false,
    preferredLocations: false, keyAchievements: false, awardsRecognition: false
  });

  // ── 1. Fetch all clients on mount ─────────────────────────────────────────
  const fetchClients = async () => {
    setLoadingClients(true);
    try {
      const response = await clientsAPI.getAllClients();
      let arr = [];
      if (Array.isArray(response)) arr = response;
      else if (response && Array.isArray(response.data)) arr = response.data;
      else if (response && typeof response === 'object') arr = [response];
      setClients(arr);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast.error('Failed to load clients');
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  };

  // ── 2. When client changes → fetch jobs for that client ───────────────────
  const handleClientChange = async (clientId) => {
    setSelectedClientId(clientId);
    // Reset job selection
    setSelectedJobId('');
    setSelectedJobCode('');
    setSelectedJobObject(null);
    setJobPostings([]);
    setScreeningQuestions([]);
    setScreeningAnswers({});

    setValidationErrors(prev => {
      const e = { ...prev };
      delete e.clientId;
      delete e.jobId;
      return e;
    });

    if (!clientId) return;

    setLoadingJobs(true);
    try {
      const response = await jobsAPI.getJobsByClientId(clientId);
      let arr = [];
      if (Array.isArray(response)) arr = response;
      else if (response && Array.isArray(response.data)) arr = response.data;
      else if (response && Array.isArray(response.jobs)) arr = response.jobs;
      else if (response && Array.isArray(response.jobPostings)) arr = response.jobPostings;
      else if (response && typeof response === 'object') arr = [response];
      setJobPostings(arr);
      if (arr.length === 0) toast.info('No jobs found for this client');
    } catch (error) {
      console.error('Error fetching jobs for client:', error);
      toast.error('Failed to load jobs for selected client');
      setJobPostings([]);
    } finally {
      setLoadingJobs(false);
    }
  };

  // ── 3. When job changes → populate job code + screening questions ─────────
  const handleJobChange = async (jobId) => {
    setSelectedJobId(jobId);
    setScreeningQuestions([]);
    setScreeningAnswers({});

    const selectedJob = jobPostings.find(job => job.id === jobId);
    if (selectedJob) {
      setSelectedJobObject(selectedJob);
      setSelectedJobCode(selectedJob.jobCode || '');
      await fetchScreeningQuestions(jobId);
    } else {
      setSelectedJobObject(null);
      setSelectedJobCode('');
    }

    setValidationErrors(prev => {
      const e = { ...prev };
      delete e.jobId;
      return e;
    });
  };

  const fetchScreeningQuestions = async (jobId) => {
    try {
      setLoading(true);
      console.log("🔍 Fetching screening questions for jobId:", jobId);
      const response = await jobsAPI.getJobById(jobId);
      let questions =
        response.data?.screeningQuestions || response.screeningQuestions ||
        response.data?.screening_questions || response.screening_questions || [];

      console.log("❓ Screening questions found:", questions.length);
      setScreeningQuestions(questions);

      const initialAnswers = {};
      questions.forEach((q, index) => { initialAnswers[`q_${index}`] = ''; });
      setScreeningAnswers(initialAnswers);
    } catch (error) {
      console.error("❌ Error fetching screening questions:", error);
      toast.error("Failed to load screening questions");
      setScreeningQuestions([]);
      setScreeningAnswers({});
    } finally {
      setLoading(false);
    }
  };

  const handleScreeningAnswerChange = (questionIndex, value) => {
    setScreeningAnswers(prev => ({ ...prev, [`q_${questionIndex}`]: value }));
    if (validationErrors[`screening_q_${questionIndex}`]) {
      setValidationErrors(prev => { const e = { ...prev }; delete e[`screening_q_${questionIndex}`]; return e; });
    }
  };

  useEffect(() => {
    const getOrgId = () => {
      let orgId = null;
      try {
        const profile = JSON.parse(localStorage.getItem("userProfile"));
        orgId = profile?.organizationId || profile?.OrganizationId;
      } catch (e) { console.error("Invalid userProfile in localStorage"); }
      if (!orgId) orgId = localStorage.getItem("organizationId");
      if (!orgId) { toast.error("Organization ID not found. Please login again."); onClose(); return null; }
      return orgId;
    };

    const orgId = getOrgId();
    if (orgId) {
      setOrganizationId(orgId);
      fetchClients();           // ← clients load first
      loadFieldConfigurations(orgId);

      // If propClientId was passed, load jobs for that client immediately
      if (propClientId) {
        handleClientChange(propClientId);
      }
    }
  }, []);

  const loadFieldConfigurations = async (orgId) => {
    setLoading(true);
    try {
      const response = await candidatesAPI.getFields();
      let configs = [];
      const docFields = {
        resumeUrl: false, coverLetterUrl: false, profilePhotoUrl: false,
        certificationsUpload: false, supportingDocuments: false,
        professionalLicenses: false, areaProfessionalCertifications: false
      };
      const specialFields = {
        technicalSkills: false, softSkills: false, languages: false,
        preferredLocations: false, keyAchievements: false, awardsRecognition: false
      };

      const rawFields = Array.isArray(response.data) ? response.data : [];

      rawFields.forEach(fieldItem => {
        const fieldName = typeof fieldItem === 'string' ? fieldItem : (fieldItem.fieldName || fieldItem.name || '');
        if (!fieldName) return;
        if (fieldName === 'resumeUrl') docFields.resumeUrl = true;
        if (fieldName === 'coverLetterUrl') docFields.coverLetterUrl = true;
        if (fieldName === 'profilePhotoUrl') docFields.profilePhotoUrl = true;
        if (fieldName === 'certificationsUpload' || fieldName === 'certifications') docFields.certificationsUpload = true;
        if (fieldName === 'supportingDocuments') docFields.supportingDocuments = true;
        if (fieldName === 'professionalLicenses') docFields.professionalLicenses = true;
        if (fieldName === 'areaProfessionalCertifications') docFields.areaProfessionalCertifications = true;
        if (fieldName === 'technicalSkills') specialFields.technicalSkills = true;
        if (fieldName === 'softSkills') specialFields.softSkills = true;
        if (fieldName === 'languages') specialFields.languages = true;
        if (fieldName === 'preferredLocations') specialFields.preferredLocations = true;
        if (fieldName === 'keyAchievements') specialFields.keyAchievements = true;
        if (fieldName === 'awardsRecognition') specialFields.awardsRecognition = true;
      });

      configs = rawFields.map((fieldItem, index) => {
        const fieldName = typeof fieldItem === 'string' ? fieldItem : (fieldItem.fieldName || fieldItem.name || '');
        const { fieldType, options } = getFieldTypeAndOptions(fieldName);
        return {
          fieldName,
          displayName: typeof fieldItem === 'object' ? (fieldItem.displayName || formatFieldName(fieldName)) : formatFieldName(fieldName),
          fieldType: typeof fieldItem === 'object' ? (fieldItem.fieldType || fieldType) : fieldType,
          isRequired: typeof fieldItem === 'object' ? (fieldItem.isRequired || false) : false,
          orderIndex: typeof fieldItem === 'object' ? (fieldItem.orderIndex ?? index) : index,
          options: typeof fieldItem === 'object' ? (fieldItem.options || options) : options,
          category: typeof fieldItem === 'object' ? (fieldItem.category || getCategoryFromFieldName(fieldName)) : getCategoryFromFieldName(fieldName),
          placeholder: typeof fieldItem === 'object' ? (fieldItem.placeholder || getPlaceholderText(fieldName)) : getPlaceholderText(fieldName),
          helpText: typeof fieldItem === 'object' ? (fieldItem.helpText || '') : '',
          rows: typeof fieldItem === 'object' ? (fieldItem.rows || 4) : 4
        };
      });

      setAvailableDocumentFields(docFields);
      setAvailableSpecialFields(specialFields);

      configs = configs.filter(field => field.fieldName && !EXCLUDED_FIELDS.includes(field.fieldName));
      configs.sort((a, b) => a.orderIndex - b.orderIndex);
      setFieldConfigs(configs);

      const initialFormData = {};
      configs.forEach(field => { initialFormData[field.fieldName] = getDefaultValue(field); });
      if (propJobId) initialFormData.jobId = propJobId;
      if (propClientId) initialFormData.clientId = propClientId;
      initialFormData.organizationId = orgId;
      setFormDataState(initialFormData);
    } catch (error) {
      console.error("Error loading field configurations:", error);
      toast.error("Failed to load form configuration");
      setFieldConfigs([]);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryFromFieldName = (fieldName) => {
    const lowerField = fieldName.toLowerCase();
    if (lowerField.includes('name') || lowerField.includes('dob') || lowerField === 'gender' ||
      lowerField === 'age' || lowerField.includes('marital') || lowerField === 'nationality') return 'Personal';
    if (lowerField.includes('email') || lowerField.includes('phone') || lowerField.includes('mobile') ||
      lowerField.includes('whatsapp') || lowerField.includes('linkedin') || lowerField.includes('github') ||
      lowerField.includes('video') || lowerField.includes('portfolio')) return 'Contact';
    if (lowerField.includes('address') || lowerField === 'city' || lowerField === 'state' ||
      lowerField === 'country' || lowerField.includes('postal') || lowerField === 'province' || lowerField === 'region') return 'Address';
    if (lowerField.includes('currentjob') || lowerField.includes('currentcompany') || lowerField.includes('previousjob') ||
      lowerField.includes('previousexp') || lowerField.includes('experience') || lowerField.includes('employment') ||
      lowerField.includes('notice') || lowerField.includes('career') || lowerField.includes('reason') || lowerField.includes('team')) return 'Employment';
    if (lowerField.includes('ctc') || lowerField.includes('salary') || lowerField.includes('compensation') ||
      lowerField.includes('currency') || lowerField.includes('frequency') || lowerField.includes('actualjoining')) return 'Compensation';
    if (lowerField.includes('education') || lowerField.includes('university') || lowerField.includes('degree') ||
      lowerField.includes('gpa') || (lowerField.includes('field') && lowerField.includes('study'))) return 'Education';
    if (lowerField.includes('visa') || (lowerField.includes('work') && lowerField.includes('auth')) || lowerField.includes('citizen')) return 'Work Authorization';
    if (lowerField.includes('reference')) return 'References';
    if (lowerField.includes('consent') || lowerField.includes('agree') || lowerField.includes('confirm') || lowerField.includes('permission')) return 'Consents';
    if (lowerField.includes('relocate') || (lowerField.includes('current') && lowerField.includes('location'))) return 'Location';
    if (lowerField.includes('source') || lowerField.includes('referral')) return 'Other';
    return 'Other';
  };

  const getPlaceholderText = (fieldName) => {
    const lowerField = fieldName.toLowerCase();
    if (lowerField.includes('email')) return 'example@email.com';
    if (lowerField.includes('phone') || lowerField.includes('mobile')) return '+91 XXXXX XXXXX';
    if (lowerField.includes('linkedin')) return 'https://linkedin.com/in/username';
    if (lowerField.includes('github')) return 'https://github.com/username';
    if (lowerField.includes('portfolio') || lowerField.includes('website')) return 'https://yourwebsite.com';
    if (lowerField.includes('city')) return 'Enter city name';
    if (lowerField.includes('experience')) return 'Years of experience';
    if (lowerField.includes('ctc') || lowerField.includes('salary')) return 'Enter amount';
    return `Enter ${formatFieldName(fieldName).toLowerCase()}...`;
  };

  const getDefaultValue = (field) => {
    if (!field) return '';
    if (BOOLEAN_FIELD_NAMES.includes(field.fieldName)) return '';
    switch (field.fieldType) {
      case 'checkbox': return false;
      case 'number': return '';
      case 'select': return '';
      case 'date': return '';
      case 'multiselect': return [];
      default: return '';
    }
  };

  const handleInputChange = (fieldName, value) => {
    setFormDataState(prev => ({ ...prev, [fieldName]: value }));
    if (validationErrors[fieldName]) {
      setValidationErrors(prev => { const e = { ...prev }; delete e[fieldName]; return e; });
    }
    if (fieldName === 'dateOfBirth' && value) {
      const birthDate = new Date(value);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
      setFormDataState(prev => ({ ...prev, age: age.toString() }));
    }
    if (['firstName', 'middleName', 'lastName'].includes(fieldName)) {
      setTimeout(() => {
        setFormDataState(prev => ({
          ...prev,
          fullName: `${prev.firstName || ''} ${prev.middleName || ''} ${prev.lastName || ''}`.trim().replace(/\s+/g, ' ')
        }));
      }, 0);
    }
  };

  const handleEmailBlur = async (fieldName, e) => {
    if (fieldName !== 'primaryEmail') return;
    const value = (e.target.value || '').trim();
    if (!value) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return;
    try {
      const res = await candidatesAPI.checkEmailExists(value);
      if (res.data?.exists) {
        setValidationErrors(prev => ({ ...prev, [fieldName]: formatDuplicateMessage(fieldName) }));
      } else {
        setValidationErrors(prev => { const e = { ...prev }; delete e[fieldName]; return e; });
      }
    } catch (error) {
      handleDuplicateError(error, (msg) => setValidationErrors(prev => ({ ...prev, [fieldName]: msg })));
    }
  };

  const handleFileUpload = (fileType, files) => {
    if (!files || files.length === 0) return;
    Array.from(files).forEach(file => {
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name}: File size should be less than 10MB`); return; }
      const fileData = { id: Date.now() + Math.random(), name: file.name, type: file.type, size: file.size, file, uploadedAt: new Date().toISOString() };
      setUploadedFiles(prev => {
        if (fileType === 'certifications' || fileType === 'supportingDocuments') {
          return { ...prev, [fileType]: [...prev[fileType], fileData] };
        }
        return { ...prev, [fileType]: fileData };
      });
      toast.success(`${file.name} uploaded successfully`);
    });
  };

  const removeFile = (fileType, fileId) => {
    if (fileType === 'certifications' || fileType === 'supportingDocuments') {
      setUploadedFiles(prev => ({ ...prev, [fileType]: prev[fileType].filter(f => f.id !== fileId) }));
    } else {
      setUploadedFiles(prev => ({ ...prev, [fileType]: null }));
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024, sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleListItemChange = (listName, index, field, value) => {
    const setters = { technicalSkills: setTechnicalSkills, softSkills: setSoftSkills, languages: setLanguages, preferredLocations: setPreferredLocations, keyAchievements: setKeyAchievements, awardsRecognition: setAwardsRecognition };
    const setter = setters[listName];
    if (!setter) return;
    setter(prev => prev.map((item, i) => i === index ? (typeof item === 'object' ? { ...item, [field]: value } : value) : item));
  };

  const addListItem = (listName) => {
    const defaults = { technicalSkills: { skillName: '', rating: 3 }, softSkills: { skillName: '', rating: 3 }, languages: { languageName: '', canRead: false, canWrite: false, canSpeak: false }, preferredLocations: '', keyAchievements: '', awardsRecognition: '' };
    const setters = { technicalSkills: setTechnicalSkills, softSkills: setSoftSkills, languages: setLanguages, preferredLocations: setPreferredLocations, keyAchievements: setKeyAchievements, awardsRecognition: setAwardsRecognition };
    const setter = setters[listName];
    if (setter && defaults[listName] !== undefined) setter(prev => [...prev, defaults[listName]]);
  };

  const removeListItem = (listName, index) => {
    const setters = { technicalSkills: setTechnicalSkills, softSkills: setSoftSkills, languages: setLanguages, preferredLocations: setPreferredLocations, keyAchievements: setKeyAchievements, awardsRecognition: setAwardsRecognition };
    const setter = setters[listName];
    if (!setter) return;
    setter(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : prev);
  };

  const validateForm = () => {
    const errors = {};
    fieldConfigs.forEach(field => {
      if (field.isRequired) {
        const value = formDataState[field.fieldName];
        const displayName = field.displayName || formatFieldName(field.fieldName);
        if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
          errors[field.fieldName] = `${displayName} is required`;
        }
      }
      if (field.fieldType === 'email' && formDataState[field.fieldName]) {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formDataState[field.fieldName])) {
          errors[field.fieldName] = 'Please enter a valid email address';
        }
      }
      if (field.fieldType === 'tel' && formDataState[field.fieldName]) {
        const phoneDigits = formDataState[field.fieldName].replace(/\D/g, '');
        if (phoneDigits.length < 10) errors[field.fieldName] = 'Please enter a valid 10-digit phone number';
      }
    });

    if (!selectedClientId) errors.clientId = 'Client selection is required';
    if (!selectedJobId) errors.jobId = 'Job selection is required';

    if (screeningQuestions.length > 0) {
      screeningQuestions.forEach((q, index) => {
        if (q.isRequired) {
          const answer = screeningAnswers[`q_${index}`];
          if (!answer || answer.trim() === '') {
            errors[`screening_q_${index}`] = `Question ${index + 1} is required`;
          }
        }
      });
    }

    if (!uploadedFiles.resume && availableDocumentFields.resumeUrl) {
      errors.resume = 'Resume is required';
    }

    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      setTimeout(() => {
        const el = document.querySelector('.border-red-500');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    }
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fill all required fields correctly');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      const candidateData = {
        organizationId,
        status: 'Applied',
        applicationId: `APP-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        jobIds: [selectedJobId],
        clientIds: [selectedClientId],
        jobId: selectedJobId,
        clientId: selectedClientId,
      };

      fieldConfigs.forEach(field => {
        const fieldName = field.fieldName;
        let raw = formDataState[fieldName];
        if (raw === null || raw === undefined) return;
        if (field.fieldType === 'date' && raw) {
          candidateData[fieldName] = typeof raw === 'string' ? raw : raw instanceof Date ? raw.toISOString().split('T')[0] : raw;
          return;
        }
        if (BOOLEAN_FIELD_NAMES.includes(fieldName)) {
          if (raw === 'Yes' || raw === true) candidateData[fieldName] = true;
          else if (raw === 'No' || raw === false) candidateData[fieldName] = false;
          else if (raw === 'Maybe') candidateData[fieldName] = null;
          return;
        }
        if (field.fieldType === 'number' && raw !== '') { candidateData[fieldName] = String(raw); return; }
        if (raw !== '') candidateData[fieldName] = raw;
      });

      if (screeningQuestions.length > 0) {
        candidateData.screeningQuestionsByJob = {
          [selectedJobId]: screeningQuestions.map((q, index) => ({
            question: q.questionText || q.question,
            answer: screeningAnswers[`q_${index}`] || ''
          }))
        };
      } else {
        candidateData.screeningQuestionsByJob = {};
      }

      candidateData.technicalSkills = technicalSkills.filter(s => s.skillName?.trim());
      candidateData.softSkills = softSkills.filter(s => s.skillName?.trim());
      candidateData.languages = languages.filter(l => l.languageName?.trim());
      candidateData.preferredLocations = preferredLocations.filter(l => l?.trim());
      candidateData.keyAchievements = keyAchievements.filter(a => a?.trim());
      candidateData.awardsRecognition = awardsRecognition.filter(a => a?.trim());

      delete candidateData.resumeUrl;
      delete candidateData.coverLetterUrl;
      delete candidateData.profilePhotoUrl;
      delete candidateData.certificationsUpload;
      delete candidateData.supportingDocuments;
      delete candidateData.professionalLicenses;
      delete candidateData.areaProfessionalCertifications;

      console.log('✅ Submitting candidateData:', JSON.stringify(candidateData, null, 2));

      formData.append('data', JSON.stringify(candidateData));
      formData.append('jobId', selectedJobId);
      formData.append('clientId', selectedClientId);

      if (uploadedFiles.resume) formData.append('resume', uploadedFiles.resume.file);
      if (uploadedFiles.coverLetter) formData.append('coverLetter', uploadedFiles.coverLetter.file);
      if (uploadedFiles.profilePhoto) formData.append('profilePhoto', uploadedFiles.profilePhoto.file);
      uploadedFiles.certifications.forEach(cert => formData.append('certifications', cert.file));
      uploadedFiles.supportingDocuments.forEach(doc => formData.append('supportingDocuments', doc.file));
      if (uploadedFiles.professionalLicenses) formData.append('professionalLicenses', uploadedFiles.professionalLicenses.file);
      if (uploadedFiles.areaCertifications) formData.append('areaCertifications', uploadedFiles.areaCertifications.file);

      const response = await candidatesAPI.createApplication(formData);
      toast.success('🎉 Candidate created successfully!');
      onSave?.(response.data);
      onClose();
    } catch (error) {
      console.error('❌ Error creating candidate:', error);
      const message = error.response?.data?.message || error.message || 'Failed to create candidate.';
      toast.error(`Error: ${message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    if (!field || !field.fieldName) return null;
    const value = formDataState[field.fieldName] ?? '';
    const error = validationErrors[field.fieldName];
    const displayName = field.displayName || formatFieldName(field.fieldName);
    const placeholderText = field.placeholder || `Enter ${displayName.toLowerCase()}...`;
    const base = "w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent transition-all duration-200";
    const errCls = "border-red-500 bg-red-50";
    const okCls = "border-gray-300 bg-white hover:border-[#1C4E6D]";
    const errorMsg = error && (
      <div className="flex items-center gap-1 text-red-600 text-sm"><AlertCircle size={14} /><span>{error}</span></div>
    );
    switch (field.fieldType) {
      case 'text': case 'email': case 'tel': case 'url':
        return (<div className="space-y-1"><input type={field.fieldType} value={value} onChange={e => handleInputChange(field.fieldName, e.target.value)} onBlur={field.fieldType === 'email' ? (e) => handleEmailBlur(field.fieldName, e) : undefined} className={`${base} ${error ? errCls : okCls}`} placeholder={placeholderText} disabled={loading} />{errorMsg}</div>);
      case 'number':
        return (<div className="space-y-1"><input type="number" value={value} onChange={e => handleInputChange(field.fieldName, e.target.value)} className={`${base} ${error ? errCls : okCls}`} placeholder={placeholderText} disabled={loading} />{errorMsg}</div>);
      case 'textarea':
        return (<div className="space-y-1"><textarea value={value} onChange={e => handleInputChange(field.fieldName, e.target.value)} className={`${base} resize-none min-h-[100px] ${error ? errCls : okCls}`} placeholder={placeholderText} disabled={loading} rows={field.rows || 4} />{errorMsg}</div>);
      case 'select':
        return (<div className="space-y-1"><select value={value} onChange={e => handleInputChange(field.fieldName, e.target.value)} className={`${base} cursor-pointer ${error ? errCls : okCls}`} disabled={loading}><option value="">Select {displayName}...</option>{field.options?.map((opt, i) => <option key={i} value={opt}>{opt}</option>)}</select>{errorMsg}</div>);
      case 'date':
        return (<div className="space-y-1"><div className="relative"><input type="date" value={value} onChange={e => handleInputChange(field.fieldName, e.target.value)} className={`${base} pr-10 ${error ? errCls : okCls}`} disabled={loading} /><Calendar className="absolute right-3 top-3.5 text-gray-400 pointer-events-none" size={20} /></div>{errorMsg}</div>);
      case 'checkbox':
        return (<div className="space-y-1"><label className="flex items-center space-x-3 cursor-pointer group"><div className={`relative w-5 h-5 rounded border flex items-center justify-center transition-all duration-200 ${error ? 'border-red-500' : 'border-gray-300 group-hover:border-[#1C4E6D]'}`}><input type="checkbox" checked={!!value} onChange={e => handleInputChange(field.fieldName, e.target.checked)} className="absolute opacity-0 w-full h-full cursor-pointer" disabled={loading} />{value && <Check size={14} className="text-[#1C4E6D]" />}</div><span className="text-gray-700 select-none text-sm">{displayName}</span></label>{errorMsg}</div>);
      case 'multiselect':
        return (<div className="space-y-1"><select multiple value={value || []} onChange={e => handleInputChange(field.fieldName, Array.from(e.target.selectedOptions, o => o.value))} className={`${base} ${error ? errCls : okCls}`} disabled={loading} size={Math.min(field.options?.length || 5, 5)}>{field.options?.map((opt, i) => <option key={i} value={opt}>{opt}</option>)}</select>{errorMsg}</div>);
      default:
        return (<input type="text" value={value} onChange={e => handleInputChange(field.fieldName, e.target.value)} className={`${base} ${error ? errCls : okCls}`} placeholder={placeholderText} disabled={loading} />);
    }
  };

  const getFieldsByCategory = (category) => fieldConfigs.filter(f => (f.category || 'Other') === category);

  const getCategories = () => {
    const order = ['Personal', 'Contact', 'Address', 'Employment', 'Compensation', 'Education', 'Work Authorization', 'Skills', 'Languages', 'Location', 'References', 'Consents', 'Documents', 'Other'];
    const unique = [...new Set(fieldConfigs.map(f => f.category || 'Other'))];
    return unique.sort((a, b) => {
      const ia = order.indexOf(a), ib = order.indexOf(b);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return a.localeCompare(b);
    });
  };

  const getCategoryIcon = (category) => {
    const map = {
      'Personal': <User size={20} className="text-[#1C4E6D]" />, 'Contact': <Mail size={20} className="text-green-600" />,
      'Address': <MapPin size={20} className="text-red-600" />, 'Employment': <Briefcase size={20} className="text-indigo-600" />,
      'Compensation': <DollarSign size={20} className="text-green-600" />, 'Education': <Award size={20} className="text-teal-600" />,
      'Work Authorization': <Shield size={20} className="text-orange-600" />, 'References': <User size={20} className="text-cyan-600" />,
      'Consents': <Shield size={20} className="text-purple-600" />, 'Skills': <Award size={20} className="text-yellow-600" />,
      'Location': <Globe size={20} className="text-orange-600" />, 'Documents': <FileText size={20} className="text-purple-600" />,
    };
    return map[category] || <FileText size={20} className="text-gray-600" />;
  };

  const renderFileUpload = (title, fileType, description, accept, multiple = false) => {
    const files = uploadedFiles[fileType];
    const hasFiles = multiple ? files.length > 0 : files !== null;
    const error = validationErrors[fileType];
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div><h4 className="font-medium text-gray-900">{title}</h4><p className="text-sm text-gray-500">{description}</p></div>
          <label className="cursor-pointer">
            <input type="file" accept={accept} multiple={multiple} onChange={e => handleFileUpload(fileType, e.target.files)} className="hidden" disabled={loading || submitting} />
            <div className={`flex items-center gap-2 px-4 py-2 bg-[#1C4E6D] text-white rounded-lg hover:bg-[#153b52] transition-colors ${(loading || submitting) ? 'opacity-50 cursor-not-allowed' : ''}`}>
              <Upload size={16} /><span className="font-medium">Upload</span>
            </div>
          </label>
        </div>
        {error && <div className="flex items-center gap-1 text-red-600 text-sm"><AlertCircle size={14} /><span>{error}</span></div>}
        {hasFiles ? (
          <div className="space-y-2">
            {multiple ? files.map(file => (
              <div key={file.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded flex items-center justify-center ${file.type.includes('image') ? 'bg-purple-100' : 'bg-blue-100'}`}>
                    {file.type.includes('image') ? <Image size={20} className="text-purple-600" /> : <FileText size={20} className="text-[#1C4E6D]" />}
                  </div>
                  <div><p className="font-medium text-gray-900 text-sm">{file.name}</p><p className="text-xs text-gray-500">{formatFileSize(file.size)}</p></div>
                </div>
                <button type="button" onClick={() => removeFile(fileType, file.id)} className="p-2 text-gray-400 hover:text-red-500 transition-colors" disabled={submitting}><Trash2 size={18} /></button>
              </div>
            )) : (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded flex items-center justify-center ${files.type.includes('image') ? 'bg-purple-100' : 'bg-blue-100'}`}>
                    {files.type.includes('image') ? <Image size={20} className="text-purple-600" /> : <FileText size={20} className="text-[#1C4E6D]" />}
                  </div>
                  <div><p className="font-medium text-gray-900 text-sm">{files.name}</p><p className="text-xs text-gray-500">{formatFileSize(files.size)}</p></div>
                </div>
                <button type="button" onClick={() => removeFile(fileType)} className="p-2 text-gray-400 hover:text-red-500 transition-colors" disabled={submitting}><Trash2 size={18} /></button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded-lg">
            <Upload className="mx-auto text-gray-400 mb-2" size={24} />
            <p className="text-sm text-gray-500">No files uploaded</p>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-end">
        <div className="absolute inset-0 bg-black/30" onClick={onClose}></div>
        <div className="relative h-screen w-full max-w-4xl bg-white shadow-2xl">
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1C4E6D] mx-auto mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-900">Loading Form Configuration...</h3>
              <p className="text-sm text-gray-500 mt-2">Fetching field configurations from API</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/30" onClick={onClose}></div>
      <div className="absolute right-0 top-0 h-screen w-full max-w-4xl bg-white shadow-2xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Create New Candidate</h2>
              <p className="text-sm text-gray-600">{fieldConfigs.length} fields loaded • {getCategories().length} categories</p>
            </div>
            <button onClick={onClose} disabled={submitting} className="p-2 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50">
              <X size={24} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Form Content */}
        <div className="p-6 space-y-8">
          {fieldConfigs.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="mx-auto text-gray-400 mb-4" size={48} />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Fields Configured</h3>
              <p className="text-gray-600">No field configurations found. Please configure fields first.</p>
              <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">Close</button>
            </div>
          ) : (
            <>
              {/* ── Job & Client Selection ─────────────────────────────────── */}
              <section className="space-y-4 bg-gray-50 p-6 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2">
                  <Briefcase size={20} className="text-[#1C4E6D]" />
                  <h3 className="text-lg font-semibold text-gray-900">Job & Client Information</h3>
                  <span className="text-red-500 ml-1">*</span>
                </div>

                {/* Row 1: Client (full width) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Client <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-1">
                    <select
                      value={selectedClientId}
                      onChange={e => handleClientChange(e.target.value)}
                      className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent transition-all cursor-pointer ${validationErrors.clientId ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white hover:border-[#1C4E6D]'}`}
                      disabled={loading || loadingClients || submitting}
                    >
                      <option value="">{loadingClients ? 'Loading clients...' : clients.length === 0 ? 'No clients available' : 'Select a client...'}</option>
                      {clients.map(client => {
                        const id = client.id || client._id || '';
                        const name = client.clientName || client.name || client.companyName || `Client ${id}`;
                        return <option key={id} value={id}>{name}</option>;
                      })}
                    </select>
                    {validationErrors.clientId && (
                      <div className="flex items-center gap-1 text-red-600 text-sm"><AlertCircle size={14} /><span>{validationErrors.clientId}</span></div>
                    )}
                    {!loadingClients && clients.length > 0 && !selectedClientId && (
                      <p className="text-xs text-gray-500">✦ Select a client to load available jobs</p>
                    )}
                    {selectedClientId && (
                      <p className="text-xs text-green-600">✓ Client selected</p>
                    )}
                  </div>
                </div>

                {/* Row 2: Job + Job Code (side by side, only shown after client selected) */}
                {selectedClientId && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Job dropdown */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Job <span className="text-red-500">*</span>
                      </label>
                      <div className="space-y-1">
                        <select
                          value={selectedJobId}
                          onChange={e => handleJobChange(e.target.value)}
                          className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent transition-all cursor-pointer ${validationErrors.jobId ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white hover:border-[#1C4E6D]'}`}
                          disabled={loading || loadingJobs || submitting}
                        >
                          <option value="">
                            {loadingJobs ? 'Loading jobs...' : jobPostings.length === 0 ? 'No jobs for this client' : 'Select a job...'}
                          </option>
                          {jobPostings.map(job => {
                            const title = job.title || job.jobTitle || job.name || `Job ${job.id}`;
                            return (
                              <option key={job.id} value={job.id}>
                                {title}
                              </option>
                            );
                          })}
                        </select>
                        {validationErrors.jobId && (
                          <div className="flex items-center gap-1 text-red-600 text-sm"><AlertCircle size={14} /><span>{validationErrors.jobId}</span></div>
                        )}
                        {!loadingJobs && jobPostings.length === 0 && (
                          <p className="text-xs text-orange-600">⚠ No jobs found for this client.</p>
                        )}
                        {!loadingJobs && jobPostings.length > 0 && (
                          <p className="text-xs text-green-600">✓ {jobPostings.length} job{jobPostings.length !== 1 ? 's' : ''} available</p>
                        )}
                      </div>
                    </div>

                    {/* Job Code (read-only, auto-populated) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Job Code</label>
                      <div className="space-y-1">
                        <input
                          type="text"
                          value={selectedJobCode}
                          readOnly
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-gray-100 text-sm cursor-not-allowed"
                          placeholder="Auto-populated when job is selected"
                        />
                        {selectedJobId && selectedJobCode && <p className="text-xs text-green-600">✓ Job code auto-populated</p>}
                        {selectedJobId && !selectedJobCode && <p className="text-xs text-gray-500">No job code for this job</p>}
                      </div>
                    </div>
                  </div>
                )}
              </section>

              {/* ── Screening Questions ───────────────────────────────────── */}
              {selectedJobObject && screeningQuestions.length > 0 && (
                <section className="space-y-4 bg-purple-50 p-6 rounded-lg border-2 border-purple-200">
                  <div className="flex items-center gap-2">
                    <AlertCircle size={20} className="text-purple-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Screening Questions</h3>
                    <span className="text-sm text-gray-600">({screeningQuestions.length} questions)</span>
                  </div>
                  <div className="space-y-4">
                    {screeningQuestions.map((question, index) => {
                      const isMultipleChoice =
                        question.questionType === 'MULTIPLE_CHOICE' || question.questionType === 'multipleChoice' ||
                        question.type === 'multiple_choice' || question.type === 'multipleChoice';
                      const questionOptions = question.options || question.answerOptions || question.choices || [];
                      const error = validationErrors[`screening_q_${index}`];
                      return (
                        <div key={index} className={`bg-white p-4 rounded-lg border-2 transition-all ${error ? 'border-red-500 bg-red-50' : 'border-purple-200'}`}>
                          <label className="block text-sm font-medium text-gray-900 mb-3">
                            <span className="text-purple-600 font-semibold">{index + 1}.</span>{' '}
                            {question.questionText || question.question}
                            {question.isRequired && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          {isMultipleChoice && questionOptions.length > 0 && (
                            <div className="text-xs text-gray-500 mb-2 italic">Select one option:</div>
                          )}
                          {isMultipleChoice && questionOptions.length > 0 ? (
                            <div className="space-y-2">
                              {questionOptions.map((option, optIndex) => (
                                <label key={optIndex} className={`flex items-center gap-3 cursor-pointer p-3 rounded-lg border-2 transition-all ${screeningAnswers[`q_${index}`] === option ? 'bg-purple-100 border-purple-500 shadow-sm' : 'bg-white border-gray-200 hover:bg-purple-50 hover:border-purple-300'}`}>
                                  <input type="radio" name={`screening_q_${index}`} value={option} checked={screeningAnswers[`q_${index}`] === option}
                                    onChange={e => handleScreeningAnswerChange(index, e.target.value)}
                                    className="w-5 h-5 text-purple-600 focus:ring-2 focus:ring-purple-500 cursor-pointer" disabled={submitting} />
                                  <span className={`text-sm flex-1 ${screeningAnswers[`q_${index}`] === option ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>{option}</span>
                                  {screeningAnswers[`q_${index}`] === option && <Check size={18} className="text-purple-600" />}
                                </label>
                              ))}
                            </div>
                          ) : (
                            <textarea value={screeningAnswers[`q_${index}`] || ''} onChange={e => handleScreeningAnswerChange(index, e.target.value)}
                              placeholder="Enter your answer here..."
                              className={`w-full px-3 py-2 rounded-lg border text-sm ${error ? 'border-red-500 bg-red-50' : 'border-gray-300 bg-white'} focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] resize-none transition-all`}
                              rows={4} disabled={submitting} />
                          )}
                          {error && <div className="flex items-center gap-1 text-red-600 text-sm mt-2 animate-pulse"><AlertCircle size={14} /><span>{error}</span></div>}
                          {question.helpText && <div className="text-xs text-gray-500 mt-2 italic">💡 {question.helpText}</div>}
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* ── Fields by category ────────────────────────────────────── */}
              {getCategories().map(category => {
                const categoryFields = getFieldsByCategory(category);
                if (categoryFields.length === 0) return null;
                return (
                  <section key={category} className="space-y-4">
                    <div className="flex items-center gap-2">
                      {getCategoryIcon(category)}
                      <h3 className="text-lg font-semibold text-gray-900">{category}</h3>
                      <span className="text-sm text-gray-500">({categoryFields.length} fields)</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {categoryFields.map(field => (
                        <div key={field.fieldName} className={field.fieldType === 'textarea' ? 'md:col-span-2' : ''}>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {field.displayName || formatFieldName(field.fieldName)}
                            {field.isRequired && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          {renderField(field)}
                          {field.helpText && <p className="text-xs text-gray-500 mt-1">{field.helpText}</p>}
                        </div>
                      ))}
                    </div>
                  </section>
                );
              })}

              {/* ── Documents ─────────────────────────────────────────────── */}
              {Object.values(availableDocumentFields).some(Boolean) && (
                <section className="space-y-6">
                  <div className="flex items-center gap-2">
                    <FileText size={20} className="text-purple-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Documents & Files</h3>
                  </div>
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {availableDocumentFields.resumeUrl && renderFileUpload('Resume/CV (Required)', 'resume', 'Upload your resume (PDF, DOC)', '.pdf,.doc,.docx')}
                      {availableDocumentFields.coverLetterUrl && renderFileUpload('Cover Letter', 'coverLetter', 'Upload cover letter (PDF, DOC)', '.pdf,.doc,.docx')}
                    </div>
                    {(availableDocumentFields.profilePhotoUrl || availableDocumentFields.professionalLicenses) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {availableDocumentFields.profilePhotoUrl && renderFileUpload('Profile Photo', 'profilePhoto', 'Upload profile picture', 'image/*')}
                        {availableDocumentFields.professionalLicenses && renderFileUpload('Professional Licenses', 'professionalLicenses', 'Upload professional licenses', '.pdf,.doc,.docx,.jpg,.png')}
                      </div>
                    )}
                    {(availableDocumentFields.certificationsUpload || availableDocumentFields.areaProfessionalCertifications) && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {availableDocumentFields.certificationsUpload && renderFileUpload('Certifications', 'certifications', 'Upload certifications (multiple allowed)', '.pdf,.doc,.docx,.jpg,.png', true)}
                        {availableDocumentFields.areaProfessionalCertifications && renderFileUpload('Area Professional Certifications', 'areaCertifications', 'Upload area professional certifications', '.pdf,.doc,.docx,.jpg,.png')}
                      </div>
                    )}
                    {availableDocumentFields.supportingDocuments && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {renderFileUpload('Supporting Documents', 'supportingDocuments', 'Upload additional documents (multiple allowed)', '*', true)}
                      </div>
                    )}
                  </div>
                </section>
              )}

              {/* ── Skills & Achievements ──────────────────────────────────── */}
              {(availableSpecialFields.technicalSkills || availableSpecialFields.softSkills || availableSpecialFields.keyAchievements || availableSpecialFields.awardsRecognition) && (
                <section className="space-y-6">
                  <div className="flex items-center gap-2">
                    <Award size={20} className="text-yellow-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Skills & Achievements</h3>
                  </div>
                  <div className="space-y-6">
                    {availableSpecialFields.technicalSkills && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-900">Technical Skills</h4>
                          <button type="button" onClick={() => addListItem('technicalSkills')} className="flex items-center gap-1 text-sm text-[#1C4E6D] hover:text-[#153b52] font-medium" disabled={submitting}><Plus size={16} />Add Skill</button>
                        </div>
                        {technicalSkills.map((skill, index) => (
                          <div key={index} className="flex items-center gap-3">
                            <input type="text" value={skill.skillName} onChange={e => handleListItemChange('technicalSkills', index, 'skillName', e.target.value)}
                              className="flex-1 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent transition-all"
                              placeholder="e.g., React, Python, AWS..." disabled={submitting} />
                            <select value={skill.rating} onChange={e => handleListItemChange('technicalSkills', index, 'rating', parseInt(e.target.value))}
                              className="w-40 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] cursor-pointer" disabled={submitting}>
                              <option value="1">⭐ Beginner</option><option value="2">⭐⭐ Basic</option>
                              <option value="3">⭐⭐⭐ Intermediate</option><option value="4">⭐⭐⭐⭐ Advanced</option>
                              <option value="5">⭐⭐⭐⭐⭐ Expert</option>
                            </select>
                            {technicalSkills.length > 1 && <button type="button" onClick={() => removeListItem('technicalSkills', index)} className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" disabled={submitting}><Trash2 size={18} /></button>}
                          </div>
                        ))}
                      </div>
                    )}
                    {availableSpecialFields.softSkills && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-900">Soft Skills</h4>
                          <button type="button" onClick={() => addListItem('softSkills')} className="flex items-center gap-1 text-sm text-[#1C4E6D] hover:text-[#153b52] font-medium" disabled={submitting}><Plus size={16} />Add Skill</button>
                        </div>
                        {softSkills.map((skill, index) => (
                          <div key={index} className="flex items-center gap-3">
                            <input type="text" value={skill.skillName} onChange={e => handleListItemChange('softSkills', index, 'skillName', e.target.value)}
                              className="flex-1 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent transition-all"
                              placeholder="e.g., Leadership, Communication..." disabled={submitting} />
                            <select value={skill.rating} onChange={e => handleListItemChange('softSkills', index, 'rating', parseInt(e.target.value))}
                              className="w-40 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] cursor-pointer" disabled={submitting}>
                              <option value="1">⭐ Beginner</option><option value="2">⭐⭐ Basic</option>
                              <option value="3">⭐⭐⭐ Intermediate</option><option value="4">⭐⭐⭐⭐ Advanced</option>
                              <option value="5">⭐⭐⭐⭐⭐ Expert</option>
                            </select>
                            {softSkills.length > 1 && <button type="button" onClick={() => removeListItem('softSkills', index)} className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" disabled={submitting}><Trash2 size={18} /></button>}
                          </div>
                        ))}
                      </div>
                    )}
                    {availableSpecialFields.keyAchievements && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-900">Key Achievements</h4>
                          <button type="button" onClick={() => addListItem('keyAchievements')} className="flex items-center gap-1 text-sm text-[#1C4E6D] hover:text-[#153b52] font-medium" disabled={submitting}><Plus size={16} />Add Achievement</button>
                        </div>
                        {keyAchievements.map((achievement, index) => (
                          <div key={index} className="flex items-start gap-3">
                            <textarea value={achievement} onChange={e => handleListItemChange('keyAchievements', index, null, e.target.value)}
                              className="flex-1 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent transition-all resize-none"
                              placeholder="Describe your achievement..." disabled={submitting} rows={2} />
                            {keyAchievements.length > 1 && <button type="button" onClick={() => removeListItem('keyAchievements', index)} className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0" disabled={submitting}><Trash2 size={18} /></button>}
                          </div>
                        ))}
                      </div>
                    )}
                    {availableSpecialFields.awardsRecognition && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-900">Awards & Recognition</h4>
                          <button type="button" onClick={() => addListItem('awardsRecognition')} className="flex items-center gap-1 text-sm text-[#1C4E6D] hover:text-[#153b52] font-medium" disabled={submitting}><Plus size={16} />Add Award</button>
                        </div>
                        {awardsRecognition.map((award, index) => (
                          <div key={index} className="flex items-center gap-3">
                            <input type="text" value={award} onChange={e => handleListItemChange('awardsRecognition', index, null, e.target.value)}
                              className="flex-1 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent transition-all"
                              placeholder="e.g., Employee of the Year 2023..." disabled={submitting} />
                            {awardsRecognition.length > 1 && <button type="button" onClick={() => removeListItem('awardsRecognition', index)} className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" disabled={submitting}><Trash2 size={18} /></button>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </section>
              )}

              {/* ── Languages ─────────────────────────────────────────────── */}
              {availableSpecialFields.languages && (
                <section className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Globe size={20} className="text-indigo-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Languages</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-900">Language Proficiency</h4>
                      <button type="button" onClick={() => addListItem('languages')} className="flex items-center gap-1 text-sm text-[#1C4E6D] hover:text-[#153b52] font-medium" disabled={submitting}><Plus size={16} />Add Language</button>
                    </div>
                    {languages.map((language, index) => (
                      <div key={index} className="p-4 border border-gray-200 rounded-lg space-y-3 bg-gray-50">
                        <div className="flex items-center gap-3">
                          <input type="text" value={language.languageName} onChange={e => handleListItemChange('languages', index, 'languageName', e.target.value)}
                            className="flex-1 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent transition-all"
                            placeholder="e.g., English, Hindi..." disabled={submitting} />
                          {languages.length > 1 && <button type="button" onClick={() => removeListItem('languages', index)} className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" disabled={submitting}><Trash2 size={18} /></button>}
                        </div>
                        <div className="flex flex-wrap gap-4">
                          {['canRead', 'canWrite', 'canSpeak'].map(cap => (
                            <label key={cap} className="flex items-center cursor-pointer hover:bg-white px-3 py-2 rounded-lg transition-colors">
                              <input type="checkbox" checked={language[cap]} onChange={e => handleListItemChange('languages', index, cap, e.target.checked)}
                                className="rounded border-gray-300 text-[#1C4E6D] focus:ring-[#1C4E6D] mr-2 w-4 h-4" disabled={submitting} />
                              <span className="text-sm text-gray-700 font-medium">{cap.replace('can', '')}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* ── Preferred Locations ───────────────────────────────────── */}
              {availableSpecialFields.preferredLocations && (
                <section className="space-y-4">
                  <div className="flex items-center gap-2">
                    <MapPin size={20} className="text-orange-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Preferred Locations</h3>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-900">Location Preferences</h4>
                      <button type="button" onClick={() => addListItem('preferredLocations')} className="flex items-center gap-1 text-sm text-[#1C4E6D] hover:text-[#153b52] font-medium" disabled={submitting}><Plus size={16} />Add Location</button>
                    </div>
                    {preferredLocations.map((location, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <input type="text" value={location} onChange={e => handleListItemChange('preferredLocations', index, null, e.target.value)}
                          className="flex-1 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent transition-all"
                          placeholder="e.g., Hyderabad, Bangalore, Remote..." disabled={submitting} />
                        {preferredLocations.length > 1 && <button type="button" onClick={() => removeListItem('preferredLocations', index)} className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" disabled={submitting}><Trash2 size={18} /></button>}
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
          <div className="flex items-center justify-end gap-3">
            <button onClick={onClose} disabled={submitting} className="px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition-colors disabled:opacity-50">Cancel</button>
            <button onClick={handleSubmit} disabled={submitting || fieldConfigs.length === 0}
              className="px-6 py-2.5 text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              style={{ backgroundColor: '#1C4E6D' }}
              onMouseEnter={e => { if (!submitting && !loading) e.currentTarget.style.backgroundColor = '#153b52'; }}
              onMouseLeave={e => { if (!submitting && !loading) e.currentTarget.style.backgroundColor = '#1C4E6D'; }}>
              {submitting ? (
                <><div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white"></div>Creating...</>
              ) : (
                <><Save size={20} />Create Candidate</>
              )}
            </button>
          </div>
          {submitting && <div className="mt-3 text-center text-sm text-[#1C4E6D]">Uploading files and creating candidate...</div>}
        </div>
      </div>
    </div>
  );
};

export default CandidateCreateModel;